import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube, Mail } from 'lucide-react';
import { SOCIAL_LINKS } from '../config/social';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer data-testid="site-footer" className="bg-gray-950 text-gray-400 py-12 border-t border-gray-800">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <picture>
                <source srcSet="/orosz-gabor-logo.webp" type="image/webp" />
                <img
                  src="/orosz-gabor-logo.png"
                  alt="Orosz Coaching logó"
                  className="h-12 w-12 rounded-lg object-contain shadow-md ring-1 ring-white/10"
                  loading="lazy"
                  decoding="async"
                  width="512"
                  height="512"
                />
              </picture>
              <div className="text-xl font-bold tracking-tight leading-none" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                <span className="text-white">OROSZ</span>
                <span className="text-yellow-600 ml-1">COACHING</span>
              </div>
            </div>
            <p className="text-sm mb-4 leading-relaxed">
              Személyre szabott edzés Miskolcon és online, tudományos alapokon és személyes figyelemmel.
            </p>
            <div className="flex gap-4">
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" data-testid="social-facebook" aria-label="Facebook" className="hover:text-yellow-400 transition-colors">
                <Facebook size={20} />
              </a>
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" data-testid="social-instagram" aria-label="Instagram" className="hover:text-yellow-400 transition-colors">
                <Instagram size={20} />
              </a>
              {SOCIAL_LINKS.youtube ? (
                <a href={SOCIAL_LINKS.youtube} target="_blank" rel="noopener noreferrer" data-testid="social-youtube" aria-label="YouTube" className="hover:text-yellow-400 transition-colors">
                  <Youtube size={20} />
                </a>
              ) : (
                <span data-testid="social-youtube" title="YouTube csatorna hamarosan" className="cursor-default opacity-60">
                  <Youtube size={20} />
                </span>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-bold mb-4">Gyors linkek</h3>
            <ul className="space-y-2 text-sm">
              <li><a href="/#about" className="hover:text-yellow-400 transition-colors">Rólam</a></li>
              <li><a href="/#programs" className="hover:text-yellow-400 transition-colors">Programok</a></li>
              <li><Link to="/szemelyi-edzo-miskolc" data-testid="footer-miskolc-link" className="hover:text-yellow-400 transition-colors">Személyi edzés Miskolc</Link></li>
              <li><Link to="/online-szemelyi-edzo" data-testid="footer-online-link" className="hover:text-yellow-400 transition-colors">Online coaching</Link></li>
              <li><a href="/#lead-magnet" className="hover:text-yellow-400 transition-colors">Ingyenes útmutató</a></li>
              <li><a href="/tudastar" className="hover:text-yellow-400 transition-colors">Tudástár</a></li>
              <li><a href="/gyik" className="hover:text-yellow-400 transition-colors">GYIK</a></li>
              <li><a href="/#contact" className="hover:text-yellow-400 transition-colors">Kapcsolat</a></li>
            </ul>
          </div>

          {/* Programs */}
          <div>
            <h3 className="text-white font-bold mb-4">Programok</h3>
            <ul className="space-y-2 text-sm">
              <li><a href="/#programs" className="hover:text-yellow-400 transition-colors">Életmódváltó Program</a></li>
              <li><a href="/#programs" className="hover:text-yellow-400 transition-colors">Izomépítés és formázás</a></li>
              <li><a href="/#programs" className="hover:text-yellow-400 transition-colors">1:1 Online Mentorálás</a></li>
              <li><a href="/#programs" className="hover:text-yellow-400 transition-colors">Kiscsoportos edzés – Duó &amp; Trió</a></li>
              <li><a href="/#contact" className="hover:text-yellow-400 transition-colors">Ingyenes konzultáció</a></li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-white font-bold mb-4">Maradj naprakész</h3>
            <p className="text-sm mb-4 leading-relaxed">
              Iratkozz fel edzéstippekért és a legfrissebb cikkekért.
            </p>
            <div className="flex gap-2">
              <input
                data-testid="footer-newsletter-input"
                type="email"
                placeholder="E-mail címed"
                className="flex-1 bg-gray-900 border border-gray-800 rounded px-3 py-2 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-yellow-600"
              />
              <button 
                data-testid="footer-newsletter-submit"
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded transition-colors"
                aria-label="Feliratkozás"
              >
                <Mail size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center text-sm">
          <div>
            © {currentYear} Orosz Coaching. Minden jog fenntartva.
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 md:mt-0">
            <Link to="/impresszum" data-testid="footer-impresszum-link" className="hover:text-yellow-400 transition-colors">Impresszum</Link>
            <Link to="/aszf" data-testid="footer-aszf-link" className="hover:text-yellow-400 transition-colors">ÁSZF</Link>
            <Link to="/adatvedelem" data-testid="footer-privacy-link" className="hover:text-yellow-400 transition-colors">Adatkezelési tájékoztató</Link>
            <Link to="/elallasi-nyilatkozat" data-testid="footer-withdrawal-link" className="hover:text-yellow-400 transition-colors">Elállási nyilatkozat</Link>
          </div>
        </div>

        {/* Barion Smart Banner — official 2025 payment-methods strip */}
        <div data-testid="footer-barion-banner" className="pt-8 mt-2 flex flex-col items-center gap-2">
          <a
            href="https://www.barion.com/hu/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Barion fizetési szolgáltató – elfogadott fizetési módok"
          >
            <img
              src="/assets/barion/barion-smart-banner-dark.svg"
              alt="Elfogadott fizetési módok: Visa, Mastercard, Maestro, Apple Pay, Google Pay – Barion"
              className="h-10 md:h-12 w-auto opacity-90 hover:opacity-100 transition-opacity"
              loading="lazy"
            />
          </a>
          <p className="text-[11px] text-gray-500 leading-relaxed text-center max-w-md">
            A bankkártyás fizetési szolgáltatást a Barion Payment Zrt. biztosítja (MNB engedély: H-EN-I-1064/2013).
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
